import React, { useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ReactFlow, {
  Background,
  Controls,
  Position,
  useNodesState,
  useEdgesState,
  Handle,
} from "reactflow";
import "reactflow/dist/style.css";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const getNodeColor = (flow, isWaitingStep) => {
  if (isWaitingStep) return "#ff5336";
  switch (flow) {
    case "Positive":
      return "#4caf50";
    case "Negative":
      return "#f44336";
    case "Optional":
      return "#ff9800";
    case "Disable":
      return "#9e9e9e";
    default:
      return "#2550d1";
  }
};

const CustomNode = ({ data, isConnectable }) => {
  const step = data.step;
  const isWaitingStep = (step.wait === "Before" || step.wait === "After") && step.waittime > 0;
  const nodeColor = getNodeColor(step.Flow, isWaitingStep);

  const renderCondition = () => {
    if (!step.condLogic && !step.condexp) return null;

    return (
      <Box
        sx={{
          mt: 0.5,
          p: 0.5,
          background: "rgba(0,0,0,0.3)",
          borderRadius: "4px",
          border: "1px solid rgba(255,255,255,0.2)",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
        }}
      >
        {step.condLogic && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontSize: "10px",
              color: "#00ff00",
              fontWeight: 700,
              mb: 0.5,
              textShadow: "0px 0px 2px rgba(0,0,0,0.5)",
            }}
          >
            {step.condLogic.toUpperCase()}
          </Typography>
        )}
        {step.condexp && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontSize: "10px",
              color: "#ffffff",
              fontFamily: "monospace",
              background: "rgba(0,0,0,0.4)",
              p: "2px 4px",
              borderRadius: "2px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              border: "1px solid rgba(255,255,255,0.1)",
              fontWeight: 500,
            }}
          >
            {step.condexp}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Tooltip
      title={
        <Box sx={{ p: 1, color: "#fff" }}>
          <Typography variant="subtitle2">Step {step.Step_Number}</Typography>
          <Typography variant="body2">Action: {step.Action}</Typography>
          <Typography variant="body2">Test Data: {step.TestData || "-"}</Typography>
          <Typography variant="body2">Flow: {step.Flow}</Typography>
          {step.condLogic && <Typography variant="body2">Condition: {step.condLogic}</Typography>}
          {step.condexp && <Typography variant="body2">Expression: {step.condexp}</Typography>}
        </Box>
      }
      arrow
      placement="top"
      enterDelay={200}
      leaveDelay={200}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.9)",
            "& .MuiTooltip-arrow": {
              color: "rgba(0, 0, 0, 0.9)",
            },
          },
        },
      }}
    >
      <Box
        sx={{
          background: nodeColor,
          color: "#fff",
          padding: "12px",
          borderRadius: "8px",
          border: "none",
          width: "180px",
          fontSize: "12px",
          fontWeight: isWaitingStep ? "bold" : "normal",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
          cursor: "move",
          position: "relative",
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        <Handle
          type="target"
          position={Position.Left}
          isConnectable={isConnectable}
          style={{
            background: "#fff",
            width: "8px",
            height: "8px",
            border: "2px solid #fff",
          }}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle2" sx={{ fontSize: "11px", fontWeight: 600 }}>
              Step {step.Step_Number}
            </Typography>
            {isWaitingStep && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  background: "rgba(0,0,0,0.2)",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  border: "1px solid rgba(255, 83, 54, 0.3)",
                }}
              >
                <AccessTimeIcon sx={{ fontSize: "14px", color: "#ff5336" }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#ff5336" }}>
                  {step.waittime}s
                </Typography>
              </Box>
            )}
          </Box>
          <Typography variant="body2" sx={{ fontSize: "11px", opacity: 0.9, fontWeight: 500 }}>
            {step.Action}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              opacity: 0.8,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              background: "rgba(0,0,0,0.1)",
              padding: "2px 4px",
              borderRadius: "4px",
            }}
          >
            {step.TestData || "-"}
          </Typography>
          {renderCondition()}
        </Box>
        <Handle
          type="source"
          position={Position.Right}
          isConnectable={isConnectable}
          style={{
            background: "#fff",
            width: "8px",
            height: "8px",
            border: "2px solid #fff",
          }}
        />
      </Box>
    </Tooltip>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

const ReviewFlowModal = ({ open, onClose, tcid, latestRecordings }) => {
  const theme = useTheme();

  const getInitialNodes = useCallback((steps) => {
    if (!steps) return [];

    const totalSteps = Object.keys(steps).length;
    const nodesPerRow = Math.min(Math.ceil(Math.sqrt(totalSteps * 1.5)), 8);

    return Object.entries(steps).map(([key, step], index) => {
      const row = Math.floor(index / nodesPerRow);
      const col = index % nodesPerRow;
      const xOffset = col * 220;
      const yOffset = row * 140;

      return {
        id: `node-${key}`,
        type: "custom",
        data: {
          step,
        },
        position: {
          x: 30 + xOffset,
          y: 20 + yOffset,
        },
        draggable: true,
      };
    });
  }, []);

  const getInitialEdges = useCallback((steps) => {
    if (!steps) return [];

    const nodesPerRow = Math.min(Math.ceil(Math.sqrt(Object.keys(steps).length * 1.5)), 8);
    const stepKeys = Object.keys(steps).sort((a, b) => steps[a].Step_Number - steps[b].Step_Number);

    return stepKeys.slice(0, -1).map((key, index) => {
      const currentRow = Math.floor(index / nodesPerRow);
      const nextRow = Math.floor((index + 1) / nodesPerRow);
      const isLastInRow = (index + 1) % nodesPerRow === 0;

      let targetKey = stepKeys[index + 1];
      if (isLastInRow && nextRow > currentRow) {
        targetKey = stepKeys[index + 1];
      }

      const sourceStep = steps[key];
      const isWaitingStep = sourceStep.wait === "Before" && sourceStep.waittime > 0;
      const edgeColor = getNodeColor(sourceStep.Flow, isWaitingStep);

      return {
        id: `edge-${key}-${targetKey}`,
        source: `node-${key}`,
        target: `node-${targetKey}`,
        type: "smoothstep",
        animated: true,
        style: {
          stroke: edgeColor,
          strokeWidth: 2,
          opacity: 0.8,
        },
        sourceHandle: "right",
        targetHandle: "left",
        markerEnd: {
          type: "arrowclosed",
          color: edgeColor,
          width: 14,
          height: 14,
        },
      };
    });
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(
    getInitialNodes(latestRecordings?.data || {}),
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    getInitialEdges(latestRecordings?.data || {}),
  );

  React.useEffect(() => {
    if (latestRecordings?.data) {
      setNodes(getInitialNodes(latestRecordings.data));
      setEdges(getInitialEdges(latestRecordings.data));
    }
  }, [latestRecordings, getInitialNodes, getInitialEdges, setNodes, setEdges]);

  const onNodeDragStop = useCallback(
    (event, node) => {
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === node.id) {
            return {
              ...n,
              position: node.position,
            };
          }
          return n;
        }),
      );
    },
    [setNodes],
  );

  const renderContent = () => {
    if (!latestRecordings?.data) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            color: "#fff",
          }}
        >
          No execution flow data available
        </Box>
      );
    }

    return (
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStop={onNodeDragStop}
        fitView
        fitViewOptions={{
          padding: 0.2,
          minZoom: 0.2,
          maxZoom: 1.5,
        }}
        defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        nodeTypes={nodeTypes}
        style={{ background: "#1a1a1a" }}
      >
        <Background color="#2a2a2a" gap={16} size={1} />
        <Controls
          style={{
            background: "#2a2a2a",
            borderRadius: "8px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
            border: "1px solid #3a3a3a",
          }}
          showInteractive={false}
        />
      </ReactFlow>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
          background: "#1a1a1a",
          color: "#fff",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#2a2a2a",
          borderBottom: "1px solid #3a3a3a",
          color: "#fff",
        }}
      >
        Review Execution Flow
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "#fff",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, background: "#1a1a1a" }}>
        <Box sx={{ height: "100%", width: "100%" }}>{renderContent()}</Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewFlowModal;
